import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useScopeEnabled } from "@/hooks/use-scope-enabled";

const buttonVariants = cva(
  "inline-flex items-center justify-center cursor-pointer gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        neutral: "bg-foreground text-background hover:bg-foreground/90",
        flat: "bg-primary/10 text-primary hover:bg-primary/20",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        "destructive-ghost":
          "text-destructive hover:bg-destructive/10 shadow-none ring-destructive",
        success:
          "bg-success text-white hover:bg-success/90 ring-success",
        "success-ghost":
          "text-success hover:bg-success/10 shadow-none ring-success",
        warning:
          "bg-warning text-white hover:bg-warning/90 ring-warning",
        "warning-ghost":
          "text-warning hover:bg-warning/10 shadow-none ring-warning",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-input",
        // Translucent hover so it reads on any surface (page, chrome, sidebar, panel).
        ghost:
          "hover:bg-muted-foreground/10 hover:text-accent-foreground data-[state=open]:bg-muted-foreground/10",
        "muted-ghost":
          "text-muted-foreground hover:bg-muted-foreground/10 hover:text-accent-foreground data-[state=open]:bg-muted-foreground/10",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3 py-2 text-xs",
        sm: "h-8 rounded-md px-2.75 text-xs [&>svg]:size-3.5",
        // Toolbar row buttons (creators table, filters).
        xs: "h-7.5 rounded-md px-2 text-xs",
        lg: "h-11 rounded-md px-8",
        icon: "h-9 w-9",
        "icon-md": "size-7.5",
        "icon-sm": "h-7 w-7",
        "icon-xs": "size-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type ScopeColor = "primary" | "destructive" | "warning" | "success" | "foreground";

/** The scope corners' press colour: the tinted variants (flat and the *-ghost tones) take their tone, the rest foreground. */
export function buttonScopeColor(variant: VariantProps<typeof buttonVariants>["variant"]): ScopeColor {
  if (variant === "flat") return "primary";
  if (variant === "destructive-ghost") return "destructive";
  if (variant === "warning-ghost") return "warning";
  if (variant === "success-ghost") return "success";
  return "foreground";
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  scope?: boolean;
  scopeColor?: ScopeColor;
  card?: boolean;
}

/**
 * @example
 * import { Plus, Trash2 } from "lucide-react"
 * import { Button } from "@/components/ui/button"
 * import { Spinner } from "@/components/ui/spinner"
 *
 * export function Toolbar({ saving, onSave, onDelete }: { saving: boolean; onSave: () => void; onDelete: () => void }) {
 *   return (
 *     <div className="flex items-center gap-2">
 *       <Button onClick={onSave} disabled={saving}>
 *         {saving && <Spinner />}
 *         Save changes
 *       </Button>
 *       <Button variant="outline" asChild>
 *         <a href="/help">Help</a>
 *       </Button>
 *       <Button variant="destructive-ghost" onClick={onDelete}>
 *         <Trash2 />
 *         Delete
 *       </Button>
 *       <Button size="icon-sm" variant="muted-ghost" aria-label="Add a creator">
 *         <Plus />
 *       </Button>
 *     </div>
 *   )
 * }
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      scope = true,
      asChild = false,
      scopeColor = buttonScopeColor(variant),
      card = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    // The scope switch is one app-wide subscription (use-scope-enabled) —
    // a creators-table page mounts hundreds of buttons, so per-instance
    // state + effects + window listeners were a real cost.
    const scopeGlobalEnabled = useScopeEnabled();

    // Determine if scope should be shown
    const shouldShowScope = scope && scopeGlobalEnabled;

    const activeClass = {
      primary: "group-active/button:border-primary",
      destructive: "group-active/button:border-destructive",
      warning: "group-active/button:border-warning",
      success: "group-active/button:border-success",
      foreground: "group-active/button:border-foreground",
    }[scopeColor];

    const ScopeBorders = () => {
      return (
        <>
          <div
            className={cn(
              "z-50 rounded-tl-[calc(var(--radius-xs)/2)] border-muted-foreground/60 absolute -top-0.75 -left-0.75 border-l border-t opacity-0 transition-all duration-100",
              "group-hover/button:opacity-100 group-hover/button:-translate-x-px group-hover/button:-translate-y-px",
              "group-active/button:translate-x-0 group-active/button:translate-y-0",
              card ? "size-2" : "size-1.5",
              activeClass
            )}
          />
          <div
            className={cn(
              "z-50 rounded-tr-[calc(var(--radius-xs)/2)] border-muted-foreground/60 absolute -top-0.75 -right-0.75 border-r border-t opacity-0 transition-all duration-100",
              "group-hover/button:opacity-100 group-hover/button:translate-x-px group-hover/button:-translate-y-px",
              "group-active/button:translate-x-0 group-active/button:translate-y-0",
              card ? "size-2" : "size-1.5",
              activeClass
            )}
          />
          <div
            className={cn(
              "z-50 rounded-bl-[calc(var(--radius-xs)/2)] border-muted-foreground/60 absolute -bottom-0.75 -left-0.75 border-l border-b opacity-0 transition-all duration-100",
              "group-hover/button:opacity-100 group-hover/button:-translate-x-px group-hover/button:translate-y-px",
              "group-active/button:translate-x-0 group-active/button:translate-y-0",
              card ? "size-2" : "size-1.5",
              activeClass
            )}
          />
          <div
            className={cn(
              "z-50 rounded-br-[calc(var(--radius-xs)/2)] border-muted-foreground/60 absolute -bottom-0.75 -right-0.75 border-r border-b opacity-0 transition-all duration-100",
              "group-hover/button:opacity-100 group-hover/button:translate-x-px group-hover/button:translate-y-px",
              "group-active/button:translate-x-0 group-active/button:translate-y-0",
              card ? "size-2" : "size-1.5",
              activeClass
            )}
          />
        </>
      );
    };

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          shouldShowScope && "group/button relative",
          className
        )}
        ref={ref}
        {...props}
      >
        {shouldShowScope && <ScopeBorders />}
        {/* asChild: Slot takes one child — Slottable marks it, and the scope
            corners land inside it instead of beside it. */}
        {asChild ? <Slottable>{props.children}</Slottable> : props.children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };